import { useContext, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import BackgroundMedia, { parseBackgroundMedia } from "../components/backgroundMedia.jsx";
import Carousel from "../components/carousel.jsx";
import ContactSection from "../components/contactSection.jsx";
import ContentText from "../components/contentText.jsx";
import LogoCarousel from "../components/LogoCarousel.jsx";
import OrganizationJsonLd from "../components/organizationJsonLd.jsx";
import SectionContent from "../components/sectionContent.jsx";
import { LayoutContext } from "../context/LayoutContext";
import { MachineContext } from "../context/MachineContext";
import { NewsContext } from "../context/NewsContext";
import { SiteContentContext } from "../context/SiteContentContext";
import { formatPublishDate } from "../dateTimeUtils.js";
import { useLocalePath } from "../hooks/useLocalePath";
import { getRecentSales } from "../httpService";
import { absoluteUrl } from "../seoUtils";
import { categoryUrlSlug } from "../slug.js";

// Intrinsic sizes for the card images. They only need to carry the right aspect ratio so the
// browser can reserve the box before the image lands (the CSS sizes the box either way):
// 3/2 for the catalogue cards, and the rendered height for the 18rem-tall news cards.
const CATEGORY_IMAGE_WIDTH = 600;
const CATEGORY_IMAGE_HEIGHT = 400;
const NEWS_IMAGE_WIDTH = 640;
const NEWS_IMAGE_HEIGHT = 288;

// The head copy, in one place. og:title and og:description still carried the old slogan after
// the title and description were rewritten, so a shared link and a search result described two
// different companies. The description is whole sentences under 155 characters — Google renders
// roughly that much and cuts mid-word otherwise. This is head copy, not page copy: the hero
// heading and every other visible string stay in site_content.
const HOME_TITLE = "AS Maskiner – leverantör av CNC-maskiner och snickerimaskiner i Sverige";
const HOME_DESCRIPTION =
  "AS Maskiner är leverantör av CNC-maskiner och snickerimaskiner i Sverige och Norden — kantlistmaskiner från KDT, sågar, putsmaskiner och spånsug.";

function HeroSection() {
  const { getContent, contentLoaded } = useContext(SiteContentContext);
  const { setTransparentHeader } = useContext(LayoutContext);

  useEffect(() => {
    setTransparentHeader(true);
    return () => setTransparentHeader(false);
  }, [setTransparentHeader]);

  const bg = parseBackgroundMedia(getContent("hero.background"));
  const bgImage = bg.image || "/images/homepage.webp";
  const bgVideo = bg.video || "";
  const overlayColor = bg.overlay?.color;
  const overlayOpacity = bg.overlay?.opacity ?? 0;

  return (
    <section className="relative h-screen -mt-16 flex flex-col items-center overflow-hidden">
      {contentLoaded && (
        <BackgroundMedia
          imageUrl={bgImage}
          videoUrl={bgVideo}
          overlayColor={overlayColor}
          overlayOpacity={overlayOpacity}
          className="absolute inset-0 h-full w-full object-cover animate-fadeIn"
          eager
        />
      )}
      {/* Hero text — upper middle */}
      <div className="relative z-10 flex-1 flex items-center justify-center text-center px-4 w-full mx-auto">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white drop-shadow-lg leading-tight mt-8">
          <ContentText contentKey="hero.heading" fallback="Innovation för framtidens snickeri" />
        </h1>
      </div>

      {/* CTA button — lower middle */}
      <div className="relative z-10 mb-[10vh] flex justify-center w-full px-4">
        <button
          type="button"
          onClick={() =>
            document.getElementById("product-catalog")?.scrollIntoView({ behavior: "smooth" })
          }
          className="inline-block border-2 border-white text-white font-bold text-lg md:text-xl px-12 py-4 rounded-full bg-black/30 hover:bg-black/50 transition-colors shadow-lg"
          data-testid="hero-cta"
        >
          <ContentText contentKey="hero.cta_text" fallback="Handla nu!" />
        </button>
      </div>
    </section>
  );
}

function ProductCatalogSection() {
  const { machineCategories } = useContext(MachineContext);
  const { localePath } = useLocalePath();

  const categoriesToShow = machineCategories.filter((c) => c.amountOfRelatedMachines > 0);

  if (categoriesToShow.length === 0) return null;

  return (
    <section id="product-catalog" className="scroll-mt-16 py-12 md:py-16 bg-surface-alt">
      <SectionContent>
        <h2 className="text-3xl md:text-4xl font-bold mb-10">
          <ContentText contentKey="catalog.heading" fallback="Produktkatalog" />
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {categoriesToShow.map((category) => (
            <Link
              key={category.slug}
              to={localePath(`/catalogue/${categoryUrlSlug(category)}`)}
              className="group block overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              data-testid={`catalog-${category.name}`}
            >
              {/* Image area — white bg supports transparent PNGs; contain shows full image */}
              <div className="aspect-[3/2] bg-white w-full">
                {category.imageUrl && (
                  <img
                    src={category.imageUrl}
                    alt={category.name}
                    width={CATEGORY_IMAGE_WIDTH}
                    height={CATEGORY_IMAGE_HEIGHT}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-contain"
                  />
                )}
              </div>
              {/* Name bar */}
              <div className="bg-white px-3 py-2">
                <h3 className="text-sm font-semibold text-text-strong truncate">{category.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </SectionContent>
    </section>
  );
}

function RecentlySoldSection() {
  const [sales, setSales] = useState([]);
  const { localePath } = useLocalePath();
  const { machines } = useContext(MachineContext);
  const { getContent, contentLoaded } = useContext(SiteContentContext);

  useEffect(() => {
    getRecentSales(12)
      .then(setSales)
      // biome-ignore lint/suspicious/noEmptyBlockStatements: silent fail
      .catch(() => {});
  }, []);

  function getSaleImage(sale) {
    if (sale.imageUrl) return sale.imageUrl;
    if (sale.machineId) {
      const machine = machines.find((m) => m.id === sale.machineId);
      if (machine?.images?.[0]) return machine.images[0];
      if (machine?.categorySlug) {
        return getContent(`category.${machine.categorySlug}.image`, "");
      }
    }
    return "";
  }

  const bg = parseBackgroundMedia(getContent("sold.background"));

  if (sales.length === 0) return null;

  const items = sales.map((sale) => ({
    imageUrl: getSaleImage(sale),
    title: sale.companyName,
    subtitle: (
      <>
        <ContentText contentKey="sold.card_prefix" fallback="investerade nyligen i" />{" "}
        {sale.machineId ? (
          <Link
            to={localePath(`/machines/${sale.machineId}`)}
            className="underline hover:opacity-80"
          >
            {sale.machineName}
          </Link>
        ) : (
          sale.machineName
        )}
      </>
    ),
  }));

  return (
    <section className="relative overflow-hidden lg:py-16 bg-white lg:bg-surface-muted">
      {/* Desktop background — hidden on mobile/tablet */}
      <div className="hidden lg:block">
        {contentLoaded && (
          <BackgroundMedia
            imageUrl={bg.image || ""}
            videoUrl={bg.video || ""}
            overlayColor={bg.overlay?.color || "#000000"}
            overlayOpacity={bg.overlay?.opacity ?? 0.55}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </div>
      <Carousel items={items} />
    </section>
  );
}

function ManufacturersSection() {
  const { getContent, contentLoaded } = useContext(SiteContentContext);

  const bg = parseBackgroundMedia(getContent("manufacturers.background"));
  const videoUrl = getContent("manufacturers.video", "");
  const logos = [1, 2, 3, 4, 5, 6, 7, 8]
    .map((n) => getContent(`manufacturers.logo_${n}`, ""))
    .filter(Boolean);

  return (
    <section className="relative py-24 md:py-36 bg-gray-900 overflow-hidden text-black">
      {contentLoaded && (
        <BackgroundMedia
          imageUrl={bg.image || ""}
          videoUrl={bg.video || ""}
          overlayColor={bg.overlay?.color || "#000000"}
          overlayOpacity={bg.overlay?.opacity ?? 0.6}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div className="relative z-10">
        {/* Heading + body */}
        <SectionContent className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <ContentText
              contentKey="manufacturers.heading"
              fallback="Intelligent investering för ert snickeri"
            />
          </h2>
          <p className="text-black/80 max-w-2xl mx-auto text-base md:text-lg">
            <ContentText contentKey="manufacturers.body" fallback="" />
          </p>
        </SectionContent>

        {/* Video player */}
        {videoUrl && (
          <SectionContent className="flex justify-center mb-10">
            <div className="w-full max-w-xl rounded-xl overflow-hidden shadow-2xl border border-black/20">
              {/* biome-ignore lint/a11y/useMediaCaption: promotional video */}
              <video src={videoUrl} controls className="w-full" preload="metadata" />
            </div>
          </SectionContent>
        )}

        {/* Logo carousel */}
        {logos.length > 0 && (
          <div className="py-8 mt-4">
            <LogoCarousel
              logos={logos.map((src, i) => ({ src, alt: `Tillverkare ${i + 1}` }))}
              speed={40}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function NewsSection() {
  const { news } = useContext(NewsContext);
  const { localePath } = useLocalePath();

  const published = news
    .filter((a) => a.published)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 3);

  if (published.length === 0) return null;

  return (
    <section className="py-12 md:py-16 bg-gray-50">
      <SectionContent>
        <h2 className="text-3xl md:text-4xl font-bold mb-10">
          <ContentText contentKey="news.heading" fallback="Nyheter" />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {published.map((article) => (
            <Link
              key={article.id}
              to={localePath(`/news/${article.id}`)}
              className="group flex flex-col shadow-sm overflow-hidden hover:shadow-md transition-shadow bg-white"
            >
              {article.imageUrl ? (
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  width={NEWS_IMAGE_WIDTH}
                  height={NEWS_IMAGE_HEIGHT}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-72 object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-full h-72 bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary/30 text-5xl">📰</span>
                </div>
              )}
              <div className="p-4 flex flex-col flex-grow bg-gray-100">
                <h3 className="font-semibold text-base group-hover:text-primary transition-colors line-clamp-3 text-text-strong">
                  {article.title}
                </h3>
                {article.createdAt && (
                  <p className="text-text-muted text-xs mt-2">
                    {formatPublishDate(article.createdAt)}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </SectionContent>
    </section>
  );
}

export default function HomePage() {
  const location = useLocation();
  const { machineCategories } = useContext(MachineContext);
  const { locale } = useLocalePath();
  const scrolledKeyRef = useRef(null);

  // When arriving with #product-catalog (e.g. the header "Produkter" link from another page),
  // scroll to that section. The section only mounts once categories load, so this re-runs on
  // machineCategories until the target exists. requestAnimationFrame defers the scroll past
  // <ScrollRestoration>, which otherwise resets to top on navigation. Guarded by location.key
  // so a later categories refetch can't re-scroll the user.
  // biome-ignore lint/correctness/useExhaustiveDependencies: machineCategories re-runs this until the section mounts
  useEffect(() => {
    if (location.hash !== "#product-catalog") return;
    if (scrolledKeyRef.current === location.key) return;
    const el = document.getElementById("product-catalog");
    if (!el) return;
    scrolledKeyRef.current = location.key;
    const raf = requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth" }));
    return () => cancelAnimationFrame(raf);
  }, [location.hash, location.key, machineCategories]);

  return (
    <>
      <Helmet>
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <meta
          name="keywords"
          content="AS Maskiner, snickerimaskin, KDT, KDT CNC, CNC fräs, Homag CNC, Biesse CNC, SCM CNC, Morbidelli CNC, KA MA, AES, Viet, Felder, Masterwood, Holz-Her, Weinig, kantlistmaskin, plattuppdelningssåg, bredbandputs, sprutautomat, fladderputs, listhyvel"
        />

        {/* Open Graph */}
        <meta property="og:url" content={absoluteUrl(locale, "/")} />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_DESCRIPTION} />

        {/* WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            url: absoluteUrl(locale, "/"),
            name: "AS Maskiner",
            description:
              "AS Maskiner - ledande leverantör av maskiner för trä- och plastindustrin i Sverige.",
          })}
        </script>
      </Helmet>

      {/* Organization + one LocalBusiness per office */}
      <OrganizationJsonLd locale={locale} />

      <HeroSection />
      <ProductCatalogSection />
      <ManufacturersSection />
      <NewsSection />
      <RecentlySoldSection />
      <ContactSection />
    </>
  );
}
